import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { User, Mail, Phone } from 'lucide-react';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import { useCreateContact, useUpdateContact, useContactQuery } from '../hooks/useQueries';
import { useToast } from '../context/ToastContext';
import { PHONE_CODE_LIST, getCountryByTimezone, getCountryByCode } from '../config/countries';

const getDefaultPhoneCode = () => {
  const code = getCountryByTimezone();
  if (code) {
    const country = getCountryByCode(code);
    if (country) return country.phoneCode;
  }
  return '+91';
};

const initialFormData = {
  name: '',
  email: '',
  mobile: '',
  phoneCode: getDefaultPhoneCode(),
  notes: ''
};

export default function ContactFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const { toast } = useToast();
  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();

  // Fetch contact if editing
  const { data: contactData } = useContactQuery(id, { enabled: !!id });
  const contact = contactData?.contact || contactData;

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);

  const isEditing = !!id;

  // Load contact data when editing
  useEffect(() => {
    if (contact && isEditing) {
      // Parse country code from stored mobile (e.g., "+91 98765" → phoneCode: "+91", mobile: "98765")
      let phoneCode = getDefaultPhoneCode();
      let mobile = contact.mobile || '';
      const matchedCode = PHONE_CODE_LIST.find(c => mobile.startsWith(c.phoneCode));
      if (matchedCode) {
        phoneCode = matchedCode.phoneCode;
        mobile = mobile.slice(matchedCode.phoneCode.length).trim();
      }
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        mobile,
        phoneCode,
        notes: contact.notes || ''
      });
    }
  }, [contact, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow digits, +, -, spaces, () in mobile field
    const sanitized = name === 'mobile' ? value.replace(/[^0-9+\-\s()]/g, '') : value;
    setFormData(prev => ({
      ...prev,
      [name]: sanitized
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (formData.mobile && !/^[0-9\s]{7,15}$/.test(formData.mobile.trim())) {
      errors.mobile = 'Please enter a valid mobile number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name.trim(),
        notes: formData.notes?.trim() || ''
      };

      if (formData.email?.trim()) {
        payload.email = formData.email.trim();
      }
      if (formData.mobile?.trim()) {
        payload.mobile = `${formData.phoneCode} ${formData.mobile.trim()}`;
      }

      if (isEditing) {
        await updateMutation.mutateAsync({ id, data: payload });
        toast.success('Contact updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Contact created successfully');
      }

      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/contacts');
      }
    } catch (err) {
      setError(err.error || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate('/contacts');
    }
  };

  return (
    <FormPageLayout title={isEditing ? 'Edit Contact' : 'Add Contact'} onBack={handleBack}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert type="error" message={error} onClose={() => setError(null)} />
        )}

        {/* Name */}
        <div>
          <label className="label">Name *</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter contact name"
              enterKeyHint="next"
              className={`input pl-10 ${validationErrors.name ? 'border-red-500' : ''}`}
            />
          </div>
          {validationErrors.name && (
            <p className="text-xs text-red-500 mt-1">{validationErrors.name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="label">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@example.com (optional)"
              enterKeyHint="next"
              className={`input pl-10 ${validationErrors.email ? 'border-red-500' : ''}`}
            />
          </div>
          {validationErrors.email && (
            <p className="text-xs text-red-500 mt-1">{validationErrors.email}</p>
          )}
          <p className="text-xs text-surface-500 mt-1">
            Email is used to send payment notifications
          </p>
        </div>

        {/* Mobile */}
        <div>
          <label className="label">Mobile</label>
          <div className="flex gap-2">
            <select
              value={formData.phoneCode}
              onChange={(e) => setFormData(prev => ({ ...prev, phoneCode: e.target.value }))}
              className="select text-sm bg-white border border-surface-200 rounded-xl w-24 flex-shrink-0"
            >
              {PHONE_CODE_LIST.map(c => (
                <option key={c.code} value={c.phoneCode}>{c.phoneCode}</option>
              ))}
            </select>
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="98765 43210 (optional)"
                enterKeyHint="next"
                className={`input pl-10 ${validationErrors.mobile ? 'border-red-500' : ''}`}
              />
            </div>
          </div>
          {validationErrors.mobile && (
            <p className="text-xs text-red-500 mt-1">{validationErrors.mobile}</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="label">Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={2}
            placeholder="Any additional notes about this contact..."
            className="input resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-surface-200">
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 px-4 py-2.5 border border-surface-200 rounded-xl text-surface-700 hover:bg-surface-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            {loading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </FormPageLayout>
  );
}
